import './CashClosingPendingDetailModal.scss';

import { Modal, Spinner } from 'react-bootstrap';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import EmployeeAPI from '../../employee/EmployeeAPI';
import { formatInReal } from '../../../commons/util/HallAppUtil';

const employeeAPI = new EmployeeAPI();

const formatDateBR = (date) => {
  if (!date) return '-';

  const [year, month, day] = String(date).split('-');
  if (!year || !month || !day) return date;

  return `${day}/${month}/${year}`;
};

const getTypeConfig = (type) => {
  if (type === 'PRODUCT') {
    return {
      label: 'Produto',
      className: 'cash-closing-pending-detail__badge--product',
      icon: '🛍️',
    };
  }

  return {
    label: 'Serviço',
    className: 'cash-closing-pending-detail__badge--service',
    icon: '✂️',
  };
};

const CashClosingPendingDetailModal = ({
  show,
  closeShow,
  companyId,
  employeeId,
  employeeName,
  startDate,
  endDate,
}) => {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');

  const loadDetail = useCallback(async () => {
    if (!show || !employeeId) return;

    try {
      setLoading(true);
      setError('');

      const response = await employeeAPI.getSummaryPendingPayDetail(
        employeeId,
        companyId,
        startDate,
        endDate
      );

      setDetail(response?.data || {});
    } catch (err) {
      console.error('Erro ao carregar detalhes do extrato:', err);
      setError('Não foi possível carregar os detalhes do extrato.');
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [show, employeeId, companyId, startDate, endDate]);

  useEffect(() => {
    if (show) {
      loadDetail();
    }
  }, [show, loadDetail]);

  const handleClose = () => {
    setDetail(null);
    setError('');
    setLoading(false);
    closeShow();
  };

  const serviceToPay = useMemo(
    () => Number(detail?.serviceToPay) || 0,
    [detail]
  );

  const productToPay = useMemo(
    () => Number(detail?.productToPay) || 0,
    [detail]
  );

  const totalAdvance = useMemo(
    () => Number(detail?.totalAdvance) || 0,
    [detail]
  );

  const totalToPay = useMemo(
    () => Number(detail?.toPay) || 0,
    [detail]
  );

  const items = useMemo(() => detail?.items || [], [detail]);

  const serviceCount = useMemo(
    () => items.filter((item) => item?.type === 'SERVICE').length,
    [items]
  );

  const productCount = useMemo(
    () => items.filter((item) => item?.type === 'PRODUCT').length,
    [items]
  );

  return (
    <Modal
      show={show}
      fullscreen
      onHide={handleClose}
      dialogClassName="cash-closing-pending-detail-modal"
    >
      <Modal.Header className="cash-closing-pending-detail__header" closeButton>
        <div className="cash-closing-pending-detail__header-content">
          <div className="cash-closing-pending-detail__avatar">
            {(detail?.employeeName || employeeName || 'P').charAt(0).toUpperCase()}
          </div>

          <div className="cash-closing-pending-detail__header-text">
            <span className="cash-closing-pending-detail__eyebrow">
              Extrato de comissão
            </span>
            <Modal.Title className="cash-closing-pending-detail__title">
              {detail?.employeeName || employeeName || 'Profissional'}
            </Modal.Title>
            <span className="cash-closing-pending-detail__subtitle">
              Visualize serviços, produtos, percentuais e total disponível para pagamento
            </span>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="cash-closing-pending-detail__body">
        {loading ? (
          <div className="cash-closing-pending-detail__state">
            <Spinner animation="border" />
            <span>Carregando detalhes...</span>
          </div>
        ) : error ? (
          <div className="cash-closing-pending-detail__state cash-closing-pending-detail__state--error">
            <strong>{error}</strong>
          </div>
        ) : (
          <div className="container cash-closing-pending-detail">
            <section className="cash-closing-pending-detail__hero">
              <div className="cash-closing-pending-detail__hero-main">
                <span className="cash-closing-pending-detail__hero-label">
                  Total a pagar
                </span>
                <strong className="cash-closing-pending-detail__hero-value">
                  {formatInReal(totalToPay)}
                </strong>
                <span className="cash-closing-pending-detail__hero-description">
                  Valor líquido considerando serviços, produtos e adiantamentos.
                </span>
              </div>

              <div className="cash-closing-pending-detail__hero-side">
                <div className="cash-closing-pending-detail__mini-info">
                  <span>Serviços</span>
                  <strong>{serviceCount}</strong>
                </div>
                <div className="cash-closing-pending-detail__mini-info">
                  <span>Produtos</span>
                  <strong>{productCount}</strong>
                </div>
                <div className="cash-closing-pending-detail__mini-info">
                  <span>Lançamentos</span>
                  <strong>{items.length}</strong>
                </div>
              </div>
            </section>

            <section className="cash-closing-pending-detail__summary-grid">
              <div className="cash-closing-pending-detail__summary-card">
                <div className="cash-closing-pending-detail__summary-icon">✂️</div>
                <div>
                  <span className="cash-closing-pending-detail__summary-label">
                    Comissão de serviços
                  </span>
                  <strong className="cash-closing-pending-detail__summary-value">
                    {formatInReal(serviceToPay)}
                  </strong>
                </div>
              </div>

              <div className="cash-closing-pending-detail__summary-card">
                <div className="cash-closing-pending-detail__summary-icon">🛍️</div>
                <div>
                  <span className="cash-closing-pending-detail__summary-label">
                    Comissão de produtos
                  </span>
                  <strong className="cash-closing-pending-detail__summary-value">
                    {formatInReal(productToPay)}
                  </strong>
                </div>
              </div>

              <div className="cash-closing-pending-detail__summary-card">
                <div className="cash-closing-pending-detail__summary-icon">💸</div>
                <div>
                  <span className="cash-closing-pending-detail__summary-label">
                    Adiantamentos
                  </span>
                  <strong className="cash-closing-pending-detail__summary-value cash-closing-pending-detail__summary-value--danger">
                    {formatInReal(totalAdvance)}
                  </strong>
                </div>
              </div>
            </section>

            <section className="cash-closing-pending-detail__extract">
              <div className="cash-closing-pending-detail__section-header">
                <div>
                  <h4>Detalhamento do extrato</h4>
                  <p>
                    Confira cada lançamento com valor, percentual e comissão calculada.
                  </p>
                </div>
              </div>

              {!items.length ? (
                <div className="cash-closing-pending-detail__empty">
                  Nenhum item encontrado para este período.
                </div>
              ) : (
                <div className="cash-closing-pending-detail__list">
                  {items.map((item, index) => {
                    const typeConfig = getTypeConfig(item?.type);

                    return (
                      <div
                        key={`${item?.type}-${item?.date}-${item?.itemName || 'item'}-${index}`}
                        className="cash-closing-pending-detail__item-card"
                      >
                        <div className="cash-closing-pending-detail__item-top">
                          <div className="cash-closing-pending-detail__item-identification">
                            <span
                              className={`cash-closing-pending-detail__badge ${typeConfig.className}`}
                            >
                              <span>{typeConfig.icon}</span>
                              {typeConfig.label}
                            </span>

                            <h5 className="cash-closing-pending-detail__item-title">
                              {item?.itemName || (item?.type === 'PRODUCT' ? 'Produto' : 'Serviço')}
                            </h5>
                          </div>

                          <span className="cash-closing-pending-detail__item-date">
                            {formatDateBR(item?.date)}
                          </span>
                        </div>

                        <div className="cash-closing-pending-detail__item-grid">
                          <div className="cash-closing-pending-detail__metric">
                            <span className="cash-closing-pending-detail__metric-label">
                              Valor base
                            </span>
                            <strong className="cash-closing-pending-detail__metric-value">
                              {formatInReal(Number(item?.value) || 0)}
                            </strong>
                          </div>

                          {item?.type === 'PRODUCT' && item?.quantity > 1 && (
                            <span className="cash-closing-pending-detail__item-subtitle">
                              {item.quantity}x de {formatInReal(item.value / item.quantity)}
                            </span>
                          )}

                          <div className="cash-closing-pending-detail__metric">
                            <span className="cash-closing-pending-detail__metric-label">
                              Percentual
                            </span>
                            <strong className="cash-closing-pending-detail__metric-value">
                              {Number(item?.percentage || 0).toFixed(2)}%
                            </strong>
                          </div>

                          <div className="cash-closing-pending-detail__metric cash-closing-pending-detail__metric--highlight">
                            <span className="cash-closing-pending-detail__metric-label">
                              Comissão gerada
                            </span>
                            <strong className="cash-closing-pending-detail__metric-value">
                              {formatInReal(Number(item?.commissionAmount) || 0)}
                            </strong>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default React.memo(CashClosingPendingDetailModal);